import { z } from 'zod';
/** A SQL Sales.id, never a checkout, invoice or short sale reference. */
export const PointPaySaleIdSchema = z
    .string()
    .uuid()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
export const POINTPAY_SALE_URL_MAX_LENGTH = 1024;
const SALE_PATH_PREFIX = '/online-payment/transactions/';
/** Deployment configuration supplies approved origins. This schema validates
 * canonical syntax only; it does not approve a host or grant access. */
export const PointPayMerchantOriginSchema = z
    .string()
    .max(POINTPAY_SALE_URL_MAX_LENGTH)
    .refine((value) => {
    try {
        const url = new URL(value);
        return (url.protocol === 'https:' &&
            url.hostname.length > 0 &&
            !url.username &&
            !url.password &&
            !url.port &&
            value === url.origin);
    }
    catch {
        return false;
    }
}, 'must be a canonical HTTPS origin without a port or trailing slash');
export const buildPointPaySalePath = (saleId) => `${SALE_PATH_PREFIX}${PointPaySaleIdSchema.parse(saleId)}`;
export const buildPointPaySaleUrl = (merchantOrigin, saleId) => {
    const value = `${PointPayMerchantOriginSchema.parse(merchantOrigin)}${buildPointPaySalePath(saleId)}`;
    if (value.length > POINTPAY_SALE_URL_MAX_LENGTH)
        throw new Error('PointPay sale URL exceeds its maximum length');
    return value;
};
const parseCanonicalSaleUrl = (value) => {
    if (typeof value !== 'string' ||
        value.length > POINTPAY_SALE_URL_MAX_LENGTH ||
        /[^\x21-\x7e]/.test(value) ||
        /[%\\]/.test(value))
        return null;
    try {
        const url = new URL(value);
        const origin = PointPayMerchantOriginSchema.safeParse(url.origin);
        if (!origin.success || !url.pathname.startsWith(SALE_PATH_PREFIX))
            return null;
        const id = PointPaySaleIdSchema.safeParse(url.pathname.slice(SALE_PATH_PREFIX.length));
        if (!id.success)
            return null;
        const canonical = buildPointPaySaleUrl(origin.data, id.data);
        // Reject every normalization, including :443, dot segments and empty ?/#.
        if (value !== canonical)
            return null;
        return { saleId: id.data, saleUrl: canonical, merchantOrigin: origin.data };
    }
    catch {
        return null;
    }
};
/** Transport syntax only. Call parsePointPaySaleUrl with trusted environment
 * origins before treating an untrusted URL as a sale locator. Never fetch it. */
export const PointPaySaleUrlSchema = z
    .string()
    .max(POINTPAY_SALE_URL_MAX_LENGTH)
    .refine((value) => parseCanonicalSaleUrl(value) !== null, 'invalid PointPay sale URL');
export const parsePointPaySaleUrl = (value, approvedOrigins) => {
    const parsed = parseCanonicalSaleUrl(value);
    if (!parsed)
        return null;
    return approvedOrigins.some((origin) => PointPayMerchantOriginSchema.safeParse(origin).success &&
        origin === parsed.merchantOrigin)
        ? parsed
        : null;
};
